import React, { ChangeEvent, Component, createRef, Fragment, RefObject } from 'react';
import Datetime from 'react-datetime';
import { DeprecatedFAIcon } from '@components-library/Icons/DeprecatedFAIcon';
import { DEPRECATED_FA_GLOBAL_SPRITE } from '@components-library/Icons/interfaces';
import { Tooltip } from '@components-library/Tooltip';
import { CONSTANTS } from '@components-shared/constants';
import { dateTimeRangeOptions } from '@components-shared/forms/DateTimeRange/dateTimeRangeOptions';
import {
  DateTimeRangeOptionType,
  IDateTimeRangeProps,
  IDateTimeRangeState,
  RangesType,
} from '@components-shared/forms/DateTimeRange/types';
import { getCurrentLanguage } from '@locales/helpers';
import { keyMap, KeyMapKeys } from '@locales/lokalise/keyMap';
import { getDefaultServerDateTime, getFormattedServerDateTime } from '@utils/library/dateTime';
import { ENUM_DATE_TIME_RANGE } from '@utils/library/dateTime/enums';
import cn from 'classnames';
import { connectToContext } from 'contextStore';
import { DateType } from 'global.interfaces';
import { observer } from 'mobx-react';
import moment from 'moment';
import { mobxStore } from 'store/mobx';

/**
 * @deprecated
 * @see library/CalendarRangeSelect
 */
class DateTimeRange extends Component<IDateTimeRangeProps, IDateTimeRangeState> {
  fromRef: RefObject<Datetime>;

  toRef: RefObject<Datetime>;

  options: DateTimeRangeOptionType[];

  mobxAccountStore = mobxStore.accountStore;

  constructor(props: IDateTimeRangeProps) {
    super(props);

    const { options, defaultValue } = this.props;

    this.options = options || dateTimeRangeOptions;

    if (defaultValue) {
      this.state = {
        valid: true,
        ...defaultValue,
      };
    } else {
      this.state = {
        valid: true,
        ...this.options[0].getRange(),
      };
    }
    this.fromRef = createRef();
    this.toRef = createRef();
  }

  componentDidMount() {
    const { defaultValue } = this.props;

    if (defaultValue) {
      this.changeHandler(this.state);
    }
  }

  static get Type() {
    return 'DateTimeRange';
  }

  getRangeForCustomDate({ from, to }: RangesType) {
    const format = (time: DateType, utcOffsetInHours: number, isServerTime?: boolean) => {
      if (!time) return '*';
      return isServerTime ? getDefaultServerDateTime(time) : getFormattedServerDateTime(time, utcOffsetInHours);
    };

    const { timeFormat, isServerTime } = this.props;
    const { utcOffsetInHours } = this.mobxAccountStore.generalSettings;
    const { range } = this.state;
    const toValue =
      range === ENUM_DATE_TIME_RANGE.custom && timeFormat === false && to
        ? format(moment(to).endOf('d'), utcOffsetInHours, isServerTime)
        : format(to, utcOffsetInHours, isServerTime);

    const fromValue = format(from, utcOffsetInHours, isServerTime);

    return `[${fromValue},${toValue}]`;
  }

  clearFrom = () => {
    const { to } = this.state;

    this.setState({ from: '' });
    this.changeHandler({ from: '', to });
  };

  clearTo = () => {
    const { from } = this.state;

    this.setState({ to: '' });
    this.changeHandler({ from, to: '' });
  };

  setFrom = (value: DateType) => {
    const { to } = this.state;

    this.setState({ from: value });
    this.validate(value, to);
    this.changeHandler({ from: value, to });
  };

  setTo = (value: DateType) => {
    const { from } = this.state;

    this.setState({ to: value });
    this.validate(from, value);
    this.changeHandler({ from, to: value });
  };

  validate(from: DateType, to: DateType) {
    if (from && to && from > to) {
      this.setState({ valid: false });
    } else {
      this.setState({ valid: true });
    }
  }

  onSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    const idx = this.options.findIndex((o) => String(o.key).toLowerCase() === e.target.value.toLowerCase());

    if (idx > -1) {
      const option = this.options[idx];

      this.setState({ ...option.getRange() });
      this.changeHandler({ ...option.getRange() });
    }
  };

  changeHandler({ from, to }: RangesType) {
    const { name, onChange } = this.props;

    if (onChange) {
      onChange({ name, value: this.getRangeForCustomDate({ from, to }) });
    }
  }

  renderClearButton = (disabled: boolean) => {
    return (
      <button type="button" className="range-picker__clear-button" disabled={disabled} onClick={this.clearTo}>
        <DeprecatedFAIcon size="sm" icon={DEPRECATED_FA_GLOBAL_SPRITE.x} />
      </button>
    );
  };

  render() {
    const { range, valid, from, to } = this.state;
    const {
      dateFormat,
      timeFormat,
      vertical,
      title,
      closeOnSelect,
      testID = 'formDateTimeRange',
      i18next,
      tooltip,
    } = this.props;
    const { DATE_FORMAT, TIME_FORMAT } = CONSTANTS;

    const disabled = !(range === ENUM_DATE_TIME_RANGE.custom);

    const className = vertical ? 'range-picker range-picker--vertical' : 'range-picker';

    const locale = getCurrentLanguage();

    const baseInputProps = {
      disabled,
      className: 'form__input',
    };

    const currentDateFormat = dateFormat != null ? dateFormat : DATE_FORMAT;
    const currentTimeFormat = timeFormat != null ? timeFormat : TIME_FORMAT;

    const renderDateRange = (initialFromValue: DateType, initialToValue: DateType, key: ENUM_DATE_TIME_RANGE) => (
      <Fragment key={key}>
        <label className="range-picker__label form__field">
          {!vertical && <span className="range-picker__label-title form__label">{i18next.t(keyMap.formsFrom)}</span>}
          <Datetime
            utc={true}
            locale={locale}
            className={valid ? '' : 'invalid'}
            inputProps={baseInputProps}
            isValidDate={(currentDate) => !(to && currentDate.startOf('day') >= to)}
            dateFormat={currentDateFormat}
            timeFormat={currentTimeFormat}
            initialValue={initialFromValue}
            onChange={this.setFrom}
            ref={this.fromRef}
            closeOnSelect={closeOnSelect}
          />
          {this.renderClearButton(disabled)}
        </label>
        <label className="range-picker__label form__field">
          {!vertical && <span className="range-picker__label-title form__label">{i18next.t(keyMap.formsTo)}</span>}
          <Datetime
            utc={true}
            locale={locale}
            className={valid ? '' : ' invalid'}
            inputProps={baseInputProps}
            isValidDate={(currentDate) => !(from && currentDate.endOf('day') <= from)}
            dateFormat={currentDateFormat}
            timeFormat={currentTimeFormat}
            initialValue={initialToValue}
            onChange={this.setTo}
            ref={this.toRef}
            closeOnSelect={closeOnSelect}
          />
          {this.renderClearButton(disabled)}
        </label>
      </Fragment>
    );

    const dateRangeComponents = this.options.map((option) => {
      if (option.key === ENUM_DATE_TIME_RANGE.custom && timeFormat === false) {
        const getRangeWithNoTimeString = (range: ReturnType<typeof option.getRange>['from']) => {
          return range.toString().slice(0, 10);
        };

        return {
          key: option.key,
          render: () =>
            renderDateRange(
              getRangeWithNoTimeString(option.getRange().from),
              getRangeWithNoTimeString(option.getRange().to),
              option.key,
            ),
        };
      }

      return {
        key: option.key,
        render: () => renderDateRange(option.getRange().from, option.getRange().to, option.key),
      };
    });

    const renderSelectedDateRange = () => {
      const element = dateRangeComponents.find((el) => el.key === range);

      if (element) {
        return element.render();
      }

      return null;
    };

    return (
      <div className={`${className} form__fieldset`} data-test-id={testID}>
        <label className="range-picker__label form__field form__field--select">
          <span className={cn('range-picker__label-title form__label', tooltip && 'form__label_tooltip')}>
            {i18next.t(title as KeyMapKeys)}
            {tooltip && <Tooltip content={tooltip} />}
          </span>
          <div className="form__select-wrapper">
            <select className="range-picker__select form__input" value={range} onChange={this.onSelect}>
              {this.options.map((o) => (
                <option key={o.key} value={o.key}>
                  {i18next.t(o.title as KeyMapKeys)}
                </option>
              ))}
            </select>
          </div>
        </label>
        {renderSelectedDateRange()}
      </div>
    );
  }
}

export default observer(connectToContext(DateTimeRange));
